
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ReportingManager.css'; // The CSS from the next step is required
import { usePortalMenuPosition, useOutsideClose } from '../../../../../hooks/usePortalDropdown';

// --- Configuration ---
// Retrieve the base URL from the .env file for centralized configuration.
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';

// --- API Endpoints ---
// Construct full endpoint paths from the base URL.
const GET_USERS_ENDPOINT = `${API_BASE_URL}/api/get-users`;
const CREATE_HIERARCHY_ENDPOINT = `${API_BASE_URL}/api/create-hierarchy`;
const NOTIFY_USER_ENDPOINT = `${API_BASE_URL}/api/notify-user`;


/**
 * A reusable dropdown component with search and clear functionality.
 */
const SearchableDropdown = ({
    options,
    value,
    onChange,
    placeholder,
    labelKey,
    valueKey,
    disabled,
    isLoading,
    renderOption,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const { menuStyle } = usePortalMenuPosition(isOpen, dropdownRef);
    useOutsideClose(isOpen, setIsOpen, dropdownRef, menuRef, () => setSearchTerm(''));

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowercasedTerm = searchTerm.toLowerCase();
        return options.filter(option => {
            const displayLabel = renderOption ? renderOption(option) : (option[labelKey] || '');
            return displayLabel.toLowerCase().includes(lowercasedTerm);
        });
    }, [options, searchTerm, labelKey, renderOption]);

    const selectedOption = useMemo(() => {
        return options.find(option => String(option[valueKey]) === String(value));
    }, [options, value, valueKey]);

    const handleSelect = (option) => {
        onChange(option ? option[valueKey] : '');
        setIsOpen(false);
        setSearchTerm('');
    };

    const toggleDropdown = () => {
        if (!disabled && !isLoading) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={`searchable-dropdown-container${isOpen ? ' is-open' : ''}`} ref={dropdownRef}>
            <div
                className={`dropdown-header ${disabled ? 'disabled' : ''} ${isLoading ? 'loading' : ''}${isOpen ? ' open' : ''}`}
                onClick={toggleDropdown}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
            >
                {selectedOption ? (
                    <span className="selected-value">{renderOption ? renderOption(selectedOption) : selectedOption[labelKey]}</span>
                ) : (
                    <span className="placeholder">{placeholder}</span>
                )}
                <div className="dropdown-controls">
                    <span className="dropdown-arrow"></span>
                </div>
            </div>
            {isOpen && createPortal(
                <div className="dropdown-list-wrapper ui-portal-dropdown-menu" style={menuStyle} ref={menuRef}>
                    <div className="dropdown-search-container ui-portal-dropdown-search">
                        <input
                            type="text"
                            className="dropdown-search-input"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                         <span className="search-icon" />
                    </div>
                    <ul className="dropdown-list">
                        {!searchTerm && (
                             <li
                                className="dropdown-list-clear-item"
                                onClick={() => handleSelect(null)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSelect(null)}
                                tabIndex={0}
                            >
                                — Clear Selection —
                            </li>
                        )}
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <li key={option[valueKey]} onClick={() => handleSelect(option)} onKeyDown={(e) => e.key === 'Enter' && handleSelect(option)} tabIndex={0}>
                                    {renderOption ? renderOption(option) : option[labelKey]}
                                </li>
                            ))
                        ) : (
                            searchTerm && <li className="no-options">No results found</li>
                        )}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};


const ReportingManager = () => {
    // --- State Management ---
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedManagerId, setSelectedManagerId] = useState('');

    // UI Feedback states
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFetchingData, setIsFetchingData] = useState(true);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [confirmation, setConfirmation] = useState({ isOpen: false });

    // --- Helper for showing notifications ---
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification({ message: '', type: '' }), 6000); // Increased duration
    };

    // --- Data Fetching ---
    const fetchUsers = useCallback(async () => {
        setIsFetchingData(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
            showNotification('Authentication error. Please log in again.', 'error');
            setIsFetchingData(false);
            return;
        }
        const headers = { 'Authorization': `Bearer ${token}` };

        try {
            const response = await fetch(GET_USERS_ENDPOINT, { headers });
            if (!response.ok) throw new Error('Failed to fetch users.');
            const data = await response.json();
             // Ensure all IDs are strings for the dropdown component
            setUsers(data.userData ? data.userData.map(u => ({ ...u, id: String(u.id) })) : []);
        } catch (error) {
            showNotification(error.message, 'error');
        } finally {
            setIsFetchingData(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // --- Form Handlers ---
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedUserId || !selectedManagerId) {
            showNotification('Please select both a user and a reporting manager.', 'error');
            return;
        }
        setConfirmation({ isOpen: true });
    };

    // --- Helper to send notification, called after successful assignment ---
    const sendAssignmentNotification = async (userToNotify, token) => {
        const url = new URL(NOTIFY_USER_ENDPOINT);
        url.searchParams.append('email', userToNotify.email);

        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            // This error will be caught by the main function
            throw new Error(errorData.message || 'Failed to send notification email.');
        }
    };

    const performAssignmentAndNotify = async () => {
        setIsSubmitting(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);

        try {
            const parentUser = users.find(u => u.id === selectedManagerId);
            const childUser = users.find(u => u.id === selectedUserId);

            if (!parentUser || !childUser) {
                throw new Error("Could not find selected user or manager. Please refresh and try again.");
            }

            // Step 1: Create the hierarchy
            const hierarchyUrl = new URL(CREATE_HIERARCHY_ENDPOINT);
            hierarchyUrl.searchParams.append('parent_user', parentUser.email);
            hierarchyUrl.searchParams.append('children_users', childUser.email);

            const hierarchyResponse = await fetch(hierarchyUrl.toString(), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!hierarchyResponse.ok) {
                const errorData = await hierarchyResponse.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to create hierarchy.');
            }

            // Step 2: Notify the user, now that assignment is successful
            try {
                await sendAssignmentNotification(childUser, token);
                showNotification(`Manager assigned to ${childUser.name} and notification sent!`, 'success');
            } catch (notificationError) {
                // Show a mixed-result message if notification fails
                showNotification(`Manager assigned successfully, but failed to send notification: ${notificationError.message}`, 'error');
            }

            // Reset form on success
            setSelectedUserId('');
            setSelectedManagerId('');

        } catch (error) {
            showNotification(error.message, 'error');
        } finally {
            setIsSubmitting(false);
            setConfirmation({ isOpen: false });
        }
    };

    // --- Filtering Logic and Placeholders for Dropdowns ---
    const availableUsers = users.filter(user => user.id !== selectedManagerId);
    const availableManagers = users.filter(user => user.id !== selectedUserId);

    const userPlaceholder = isFetchingData
        ? "Loading users..."
        : (users.length === 0 ? "No users available" : "-- Select a User --");

    const managerPlaceholder = isFetchingData
        ? "Loading managers..."
        : (users.length === 0 ? "No managers available" : "-- Select a Reporting Manager --");

    const renderUserOption = (user) => `${user.name} (${user.email})`;
    
    // Find full user objects for display
    const selectedUser = users.find(u => u.id === selectedUserId);
    const selectedManager = users.find(u => u.id === selectedManagerId);

    return (
        <div className="rm-container">
            {notification.message && (
                <div className={`rm-notification ${notification.type}`}>{notification.message}</div>
            )}

            {confirmation.isOpen && (
                <div className="rm-modal-overlay" onClick={() => !isSubmitting && setConfirmation({ isOpen: false })}>
                    <div className="rm-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="rm-modal-header"><h3>Confirm Assignment & Notification</h3></div>
                        <div className="rm-modal-body">
                            Are you sure you want to assign <strong>{selectedManager?.name}</strong> as the manager for <strong>{selectedUser?.name}</strong>?
                            <br/><br/>
                            An activation and notification email will be sent to the user.
                        </div>
                        <div className="rm-modal-footer">
                            <button type="button" className="rm-button secondary" onClick={() => setConfirmation({ isOpen: false })} disabled={isSubmitting}>Cancel</button>
                            <button type="button" className="rm-button success" onClick={performAssignmentAndNotify} disabled={isSubmitting}>
                                {isSubmitting ? 'Processing...' : 'Yes, Assign & Notify'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="rm-page-header">
                <div>
                    <h2 className="rm-page-title">Manage Reporting Managers</h2>
                    <p className="rm-page-subtitle">Assign reporting managers to users.</p>
                </div>
            </div>

            <div className="rm-content-card">
                <form onSubmit={handleSubmit}>
                    <h3 className="rm-card-title">Assign Reporting Manager to User</h3>

                    <div className="rm-form-group">
                        <label className="rm-form-label">Select User *</label>
                         <SearchableDropdown
                            options={availableUsers}
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            placeholder={userPlaceholder}
                            labelKey="name"
                            valueKey="id"
                            disabled={isFetchingData || isSubmitting}
                            isLoading={isFetchingData}
                            renderOption={renderUserOption}
                        />
                    </div>

                    <div className="rm-form-group">
                        <label className="rm-form-label">Select Reporting Manager *</label>
                        <SearchableDropdown
                            options={availableManagers}
                            value={selectedManagerId}
                            onChange={setSelectedManagerId}
                            placeholder={managerPlaceholder}
                            labelKey="name"
                            valueKey="id"
                            disabled={isFetchingData || isSubmitting}
                            isLoading={isFetchingData}
                            renderOption={renderUserOption}
                        />
                    </div>

                    <button type="submit" className="rm-button success" disabled={isSubmitting || isFetchingData || !selectedUserId || !selectedManagerId}>
                        Assign & Notify
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ReportingManager;